import { LocalStorage } from 'quasar'
import DejavooDeepLink from './DejavooDeepLink'
import { getCachedAndroidDeviceFlags } from './androidDeviceProfile'
import {
  BLU_BRAND_DEFAULTS,
  buildCustomUi,
  buildRegisterPayload,
  enrichDvPayLitePayload,
  resolveDvPayLiteBranding,
  stripHexForDvPay
} from './dvPayLitePayload'

export {
  BLU_BRAND_DEFAULTS,
  buildCustomUi,
  buildRegisterPayload,
  enrichDvPayLitePayload,
  resolveDvPayLiteBranding,
  stripHexForDvPay
}

/** Bump when register payload shape changes (e.g. customUI on addTerminal). */
const REGISTERED_KEY_PREFIX = 'dvpaylite_registered_v2_'

/** Dejavoo doc-example customUI — PoC / support A/B only (not used in checkout). */
const DOC_EXAMPLE_CUSTOM_UI = {
  fontFamily: 'open sans',
  primaryColor: '3c6850',
  secondaryColor: '000000',
  negativeButtonColor: 'Ed3e3e',
  removeLoaderLogo: 'YES',
  requiredAvs: 'NO'
}

let cachedIsvId = null
let isvIdFetchInflight = null

/**
 * @returns {object|undefined}
 */
export function getDvPayLitePlugin () {
  if (typeof window === 'undefined') {
    return undefined
  }
  return window.Capacitor?.Plugins?.DvPayLite
}

export function isIntentTransportAvailable () {
  return typeof getDvPayLitePlugin()?.performTransaction === 'function'
}

export function isUriTransportAvailable () {
  if (typeof window === 'undefined') {
    return false
  }
  return typeof window.Capacitor?.Plugins?.DvPayLauncher?.openForResult === 'function'
}

/** Integrated terminal checkout when Intent SDK or URI launcher is present on Dejavoo hardware. */
export function isIntegratedTerminalSupported (options = {}) {
  if (typeof window === 'undefined') {
    return false
  }
  const cap = window.Capacitor
  if (!cap?.isNativePlatform?.() || cap.getPlatform?.() !== 'android') {
    return false
  }
  const pluginOk = isIntentTransportAvailable() || isUriTransportAvailable()
  if (!pluginOk) {
    return false
  }
  if (options.pocBypass) {
    return true
  }
  const flags = getCachedAndroidDeviceFlags()
  if (!flags.loaded) {
    return false
  }
  return flags.supportsDvPayLiteIntegrated
}

/**
 * @param {object} payload
 * @param {'intent'|'uri'|'auto'} [transport]
 */
export function buildPayloadDigest (payload, transport = 'auto') {
  const resolvedTransport = transport === 'auto'
    ? (isIntentTransportAvailable() ? 'intent' : 'uri')
    : transport
  const customUI = payload?.customUI
  const payloadJson = JSON.stringify(payload ?? {})
  return {
    transport: resolvedTransport,
    type: payload?.type ?? null,
    refId: payload?.refId ?? null,
    amount: payload?.amount ?? null,
    tip: payload?.tip ?? null,
    showBreakupScreen: payload?.showBreakupScreen ?? null,
    showTipScreen: payload?.showTipScreen ?? null,
    showDualPriceScreen: payload?.showDualPriceScreen ?? null,
    isTxnStatusScreenRequired: payload?.isTxnStatusScreenRequired ?? null,
    hasIsvId: !!(payload?.IsvId),
    isvId: payload?.IsvId ? String(payload.IsvId) : null,
    hasCustomUI: !!customUI,
    customUI: customUI ? { ...customUI } : null,
    payloadJsonLength: payloadJson.length
  }
}

/**
 * Summarize SDK diagnostics to spot config vs payload issues.
 * @param {object|null|undefined} diagnostics
 */
export function analyzeLaunchDiagnostics (diagnostics) {
  if (!diagnostics || typeof diagnostics !== 'object') {
    return {
      hasLaunchAck: false,
      hasLaunchFailed: false,
      hints: ['No SDK diagnostics returned — confirm Intent transport is active.']
    }
  }

  const hints = []
  const launchAck = diagnostics.launchAck
  const launchFailed = diagnostics.launchFailed

  if (launchFailed) {
    hints.push('DvPayLite rejected launch — check launchFailed in diagnostics.')
  }
  if (launchAck == null && !launchFailed) {
    hints.push('No launchAck callback — DvPayLite may not have acknowledged the request.')
  }
  if (diagnostics.launchAckEchoesCustomUI === false) {
    hints.push('launchAck did not echo customUI — DvPayLite app may be too old or ignoring customUI.')
  }
  if (diagnostics.requestHasCustomUI === true && diagnostics.launchAckEchoesCustomUI === false) {
    hints.push('Payload included customUI but launchAck did not reference it.')
  }
  if (diagnostics.requestHasCustomUI === false) {
    hints.push('Payload sent without customUI object.')
  }
  const requestJson = typeof diagnostics.requestPayloadJson === 'string'
    ? diagnostics.requestPayloadJson
    : ''
  if (requestJson.includes('"showBreakupScreen":"No"') || requestJson.includes('"showBreakupScreen": "No"')) {
    hints.push('showBreakupScreen is No — if breakup still appears, disable Breakup in STEAM/TPN config (Dejavoo docs).')
  }
  if (requestJson && !requestJson.includes('IsvId') && diagnostics.requestHasCustomUI === true) {
    hints.push('IsvId missing from payload — Dejavoo may require ISV registration for customUI.')
  }

  return {
    hasLaunchAck: launchAck != null,
    hasLaunchFailed: !!launchFailed,
    launchAckEchoesCustomUI: diagnostics.launchAckEchoesCustomUI ?? null,
    launchAckEchoesShowBreakupScreen: diagnostics.launchAckEchoesShowBreakupScreen ?? null,
    requestHasCustomUI: diagnostics.requestHasCustomUI ?? null,
    launchAckKeys: diagnostics.launchAckKeys ?? null,
    hints
  }
}

/**
 * Payload matching Dejavoo customUI doc example (for A/B testing on terminal).
 * @param {object} params
 * @param {number|string} params.saleId
 * @param {number} params.amount
 * @param {number} [params.tipAmount]
 * @param {string|null} [params.isvId]
 * @param {string|null} [params.tpn]
 */
export function buildDocExampleSalePayload ({
  saleId,
  amount,
  tipAmount = 0,
  isvId = null,
  tpn = null,
  businessInfo = null
}) {
  const payload = enrichDvPayLitePayload({
    type: 'SALE',
    paymentType: 'Credit',
    amount: formatDvPayMoney(amount),
    tip: formatDvPayMoney(tipAmount),
    applicationType: 'DVPAYLITE',
    refId: String(saleId),
    receiptType: 'No',
    isTxnStatusScreenRequired: 'No',
    showTipScreen: 'No',
    showBreakupScreen: 'No',
    showDualPriceScreen: 'No',
    ...(tpn ? { TPN: String(tpn) } : {})
  }, { businessInfo, isvId })
  payload.customUI = { ...DOC_EXAMPLE_CUSTOM_UI }
  return payload
}

/**
 * @param {object|null|undefined} raw
 * @returns {{ transactionResultJson?: string, transactionResult?: string, diagnostics?: object, resultCode?: number }}
 */
export function normalizeIntentPluginResult (raw) {
  if (!raw) {
    return {}
  }
  return {
    transactionResultJson: raw.transactionResultJson,
    transactionResult: raw.transactionResult,
    diagnostics: raw.diagnostics ?? null,
    resultCode: raw.resultCode
  }
}

async function launchIntegratedSaleViaIntent (payload) {
  const plugin = getDvPayLitePlugin()
  const startedAt = Date.now()
  const raw = await plugin.performTransaction({ payload: JSON.stringify(payload) })
  const pluginResult = normalizeIntentPluginResult(raw)
  const diagnostics = pluginResult.diagnostics ?? null
  return {
    launched: true,
    method: 'DvPayLite.performTransaction',
    pluginResult,
    diagnostics,
    launchAnalysis: analyzeLaunchDiagnostics(diagnostics),
    payloadDigest: buildPayloadDigest(payload, 'intent'),
    startedAt,
    endedAt: Date.now()
  }
}

async function launchIntegratedSaleViaUri (payload) {
  const res = await DejavooDeepLink.launch(
    DejavooDeepLink.DejavooActions.Sale,
    { data: JSON.stringify(payload) }
  )
  return {
    ...res,
    diagnostics: null,
    payloadDigest: buildPayloadDigest(payload, 'uri')
  }
}

async function registerTerminalViaIntent (registerPayload) {
  const plugin = getDvPayLitePlugin()
  const raw = await plugin.addTerminal({ payload: JSON.stringify(registerPayload) })
  return {
    launched: true,
    method: 'DvPayLite.addTerminal',
    pluginResult: normalizeIntentPluginResult(raw),
    diagnostics: raw?.diagnostics ?? null
  }
}

async function registerTerminalViaUri (registerPayload) {
  return DejavooDeepLink.launch(
    DejavooDeepLink.DejavooActions.DeviceRegister,
    { data: JSON.stringify(registerPayload) }
  )
}

export function formatDvPayMoney (value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) {
    return '0.00'
  }
  return n.toFixed(2)
}

/**
 * @param {object} params
 * @param {number|string} params.saleId
 * @param {number} params.amount Sale total before tip
 * @param {number} [params.tipAmount]
 * @param {object|null} [params.businessInfo]
 * @param {string|null} [params.tpn]
 * @param {string|null} [params.isvId]
 * @param {string|null} [params.merchantId]
 */
export function buildSalePayload ({
  saleId,
  amount,
  tipAmount = 0,
  businessInfo = null,
  tpn = null,
  isvId = null,
  merchantId = null
}) {
  const payload = enrichDvPayLitePayload({
    type: 'SALE',
    paymentType: 'CREDIT',
    amount: formatDvPayMoney(amount),
    tip: formatDvPayMoney(tipAmount),
    applicationType: 'DVPAYLITE',
    refId: String(saleId),
    receiptType: 'No',
    isTxnStatusScreenRequired: 'No',
    showTipScreen: 'No',
    showBreakupScreen: 'No',
    showDualPriceScreen: 'No',
    ...(tpn ? { TPN: String(tpn) } : {}),
    ...(merchantId ? { MerchantId: String(merchantId) } : {})
  }, { businessInfo, isvId })
  return payload
}

/**
 * PRE_AUTH payload for integrated card-on-file capture (opening hold or $1 wallet verify).
 *
 * @param {object} params
 * @param {string} params.refId
 * @param {number} params.amountDollars
 * @param {object|null} [params.businessInfo]
 * @param {string|null} [params.tpn]
 * @param {string|null} [params.isvId]
 */
export function buildPreAuthCapturePayload ({
  refId,
  amountDollars,
  businessInfo = null,
  tpn = null,
  isvId = null
}) {
  return enrichDvPayLitePayload({
    type: 'PRE_AUTH',
    paymentType: 'CREDIT',
    amount: formatDvPayMoney(amountDollars),
    tip: formatDvPayMoney(0),
    applicationType: 'DVPAYLITE',
    refId: String(refId),
    receiptType: 'No',
    isTxnStatusScreenRequired: 'No',
    showTipScreen: 'No',
    showBreakupScreen: 'No',
    showDualPriceScreen: 'No',
    ...(tpn ? { TPN: String(tpn) } : {})
  }, { businessInfo, isvId })
}

function parsePluginResultJson (pluginResult) {
  if (!pluginResult) {
    return null
  }
  try {
    return pluginResult.transactionResultJson
      ? JSON.parse(pluginResult.transactionResultJson)
      : null
  } catch {
    return null
  }
}

/**
 * @param {object|null|undefined} pluginResult
 */
export function parseDvPayLitePluginResult (pluginResult) {
  const parsed = parsePluginResultJson(pluginResult)
  const txnResult = typeof pluginResult?.transactionResult === 'string'
    ? pluginResult.transactionResult
    : ''
  const respCode = parsed?.respCode || parsed?.HostResponseCode || parsed?.hostResponseCode
  const isSuccess = String(respCode) === '00'
    || (/^success$/i.test(txnResult) && !parsed?.error_code && !parsed?.error_message)
  const errorCode = parsed?.error_code
  const errorMessage = parsed?.error_message
    || parsed?.HostResponseMessage
    || parsed?.message
    || null
  const isCancelled = (typeof errorCode === 'number' && errorCode === 18)
    || /cancel/i.test(String(errorMessage || ''))

  return {
    parsed,
    txnResult,
    respCode,
    isSuccess,
    isCancelled,
    errorMessage: errorMessage || txnResult || null
  }
}

function isTerminalRegisterSuccess (pluginResult, parsed) {
  if (parsed?.isValidTPN === true) {
    return true
  }
  const msg = String(parsed?.message || pluginResult?.transactionResult || '')
  return /^success$/i.test(msg) || /terminal added successfully/i.test(msg)
}

function registeredStorageKey (tpn) {
  return `${REGISTERED_KEY_PREFIX}${String(tpn).trim()}`
}

export function isTerminalRegisteredLocally (tpn) {
  if (!tpn) {
    return false
  }
  return LocalStorage.getItem(registeredStorageKey(tpn)) === true
}

export function markTerminalRegisteredLocally (tpn) {
  if (!tpn) {
    return
  }
  LocalStorage.set(registeredStorageKey(tpn), true)
}

/**
 * @param {string} tpn
 * @param {object} [options]
 * @param {object|null} [options.businessInfo]
 * @param {string|null} [options.isvId]
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function ensureTerminalRegistered (tpn, { businessInfo = null, isvId = null } = {}) {
  const normalizedTpn = String(tpn || '').trim()
  if (!normalizedTpn) {
    return { ok: false, message: 'terminal_not_configured' }
  }
  if (isTerminalRegisteredLocally(normalizedTpn)) {
    return { ok: true }
  }

  const registerPayload = buildRegisterPayload({
    tpn: normalizedTpn,
    businessInfo,
    isvId
  })

  const res = isIntentTransportAvailable()
    ? await registerTerminalViaIntent(registerPayload)
    : await registerTerminalViaUri(registerPayload)

  if (!res?.launched) {
    return { ok: false, message: 'register_launch_failed' }
  }

  const parsed = parsePluginResultJson(res?.pluginResult)
  if (isTerminalRegisterSuccess(res?.pluginResult, parsed)) {
    markTerminalRegisteredLocally(normalizedTpn)
    return { ok: true }
  }

  const err = parsed?.error_message || res?.pluginResult?.transactionResult || 'register_failed'
  return { ok: false, message: String(err) }
}

/**
 * @returns {Promise<string|null>}
 */
export async function fetchDvPayLiteIsvId () {
  if (cachedIsvId) {
    return cachedIsvId
  }
  if (isvIdFetchInflight) {
    return isvIdFetchInflight
  }

  isvIdFetchInflight = (async () => {
    try {
      const { businessInfoService } = await import('./BusinessInfoService')
      const cfg = await businessInfoService.getDejavooTopConfig()
      const id = cfg?.isv_id ? String(cfg.isv_id).trim() : ''
      cachedIsvId = id || null
      return cachedIsvId
    } catch {
      cachedIsvId = null
      return null
    } finally {
      isvIdFetchInflight = null
    }
  })()

  return isvIdFetchInflight
}

/**
 * @param {object} payload
 * @param {{ transport?: 'intent'|'uri'|'auto' }} [options]
 */
export async function launchIntegratedSale (payload, options = {}) {
  const transport = options.transport ?? 'auto'

  if (transport === 'uri' || (transport === 'auto' && !isIntentTransportAvailable())) {
    if (transport === 'uri' && !isUriTransportAvailable()) {
      throw new Error('DvPayLite URI transport is not available')
    }
    return launchIntegratedSaleViaUri(payload)
  }

  if (!isIntentTransportAvailable()) {
    throw new Error('DvPayLite Intent transport is not available')
  }
  return launchIntegratedSaleViaIntent(payload)
}

/**
 * @param {object} params
 * @param {number|string} params.saleId
 * @param {object|null} params.parsed
 * @param {number} params.fallbackAmount
 * @param {string} [params.notes]
 */
export function buildExternalPaymentRecord ({
  saleId,
  parsed,
  fallbackAmount,
  notes = 'Recorded from Dejavoo integrated terminal'
}) {
  const reference = parsed?.rrn || parsed?.transaction_id || parsed?.transId || parsed?.iPOSToken || null
  const brand = parsed?.card_type
    || (parsed?.emvAppName ? String(parsed.emvAppName).split(' ')[0] : null)
  const respCode = parsed?.respCode || parsed?.HostResponseCode || parsed?.hostResponseCode
  const last4 = parsed?.last_4_digits
    || (parsed?.mask_pan ? String(parsed.mask_pan).slice(-4) : null)
  const authCode = parsed?.authCode || null
  const token = parsed?.iPOSToken || null
  const tipAmount = Number(parsed?.tipAmount || 0)
  const feeAmount = parsed?.fee != null ? Number(parsed.fee) : null
  const ltax = Number(parsed?.LTaxAmount || 0)
  const stax = Number(parsed?.STaxAmount || 0)
  const saleTax = ltax + stax
  const totalAmount = parsed?.totalAmount != null
    ? Number(parsed.totalAmount)
    : Number(fallbackAmount || 0)

  const metadata = {
    respCode,
    respMsg: parsed?.respMsg,
    status: parsed?.status,
    transaction_title: parsed?.transaction_title,
    transaction_mode: parsed?.transaction_mode,
    transaction_type: parsed?.transaction_type,
    amount_paid: parsed?.amount_paid,
    batchNo: parsed?.batchNo,
    rrn: parsed?.rrn,
    fee: feeAmount,
    emv: {
      tvr: parsed?.emvTVR,
      tc: parsed?.emvTC,
      tsi: parsed?.emvTSI,
      aid: parsed?.emvAID,
      appName: parsed?.emvAppName
    },
    cardHolderName: parsed?.cardHolderName,
    expDate: parsed?.expDate,
    mask_pan: parsed?.mask_pan,
    tipLine: parsed?.tipLine,
    pinMode: parsed?.pinMode,
    date: parsed?.date,
    time: parsed?.time,
    iPOSToken: parsed?.iPOSToken,
    invoice_no: parsed?.invoice_no,
    extData: parsed?.extData
  }

  return {
    saleId,
    amount: totalAmount,
    tipAmount,
    taxAmount: saleTax,
    method: 'card',
    gateway: 'dejavoo',
    gatewayReference: reference,
    gatewayMetadata: metadata,
    notes,
    fee_amount: feeAmount,
    sale_total: totalAmount,
    sale_tax: saleTax,
    card_brand: brand,
    card_last4: last4,
    auth_code: authCode,
    rrn: parsed?.rrn || null,
    transaction_id: parsed?.transaction_id || parsed?.transId || null,
    token,
    payment: {
      gateway: 'dejavoo',
      gateway_reference: reference,
      amount: totalAmount,
      tax_amount: saleTax,
      tip_amount: tipAmount,
      card_brand: brand,
      card_last4: last4,
      authorization_code: authCode,
      token
    }
  }
}

/**
 * @param {object} params
 * @param {number|string} params.saleId
 * @param {object|null} params.parsed
 * @param {number} params.fallbackAmount
 * @param {string} [params.notes]
 */
export async function recordDvPayLiteExternalPayment (params) {
  const record = buildExternalPaymentRecord(params)
  const { paymentService } = await import('./PaymentService')
  await paymentService.recordExternalPayment(record)
  return record
}

/**
 * Full integrated sale: register terminal, build payload, launch deeplink.
 *
 * @param {object} params
 * @param {number|string} params.saleId
 * @param {number} params.saleTotal Full sale total including tax (before tip)
 * @param {number} [params.tipAmount]
 * @param {string|null} [params.tpn]
 * @param {object|null} [params.businessInfo]
 * @param {(() => void)|null} [params.onHandoff] Called immediately before opening DvPayLite
 */
export async function runIntegratedSaleFlow ({
  saleId,
  saleTotal,
  tipAmount = 0,
  tpn = null,
  businessInfo = null,
  onHandoff = null
}) {
  const isvId = await fetchDvPayLiteIsvId()
  if (tpn) {
    const reg = await ensureTerminalRegistered(tpn, { businessInfo, isvId })
    if (!reg.ok) {
      return { ok: false, stage: 'register', message: reg.message }
    }
  }

  const amount = Math.max(0, Number(saleTotal || 0))
  const payload = buildSalePayload({
    saleId,
    amount,
    tipAmount,
    businessInfo,
    tpn,
    isvId
  })

  onHandoff?.()
  const launchResult = await launchIntegratedSale(payload)
  return { ok: true, launchResult, payload }
}

/**
 * Interpret launch result after integrated sale deeplink returns.
 *
 * @param {object} params
 * @param {object|null} params.launchResult
 * @param {number|string} params.saleId
 * @param {number} params.fallbackAmount
 * @param {number} [params.fallbackTipAmount]
 * @param {string} [params.notes]
 */
export function interpretIntegratedLaunchResult ({
  launchResult,
  saleId,
  fallbackAmount,
  fallbackTipAmount = 0,
  notes
}) {
  if (!launchResult?.pluginResult) {
    return {
      kind: 'handoff',
      launched: !!launchResult?.launched,
      method: launchResult?.method || null
    }
  }

  const outcome = parseDvPayLitePluginResult(launchResult.pluginResult)
  if (outcome.isSuccess) {
    const totalFallback = Number(fallbackAmount || 0) + Number(fallbackTipAmount || 0)
    return {
      kind: 'success',
      outcome,
      record: buildExternalPaymentRecord({
        saleId,
        parsed: outcome.parsed,
        fallbackAmount: totalFallback,
        notes
      })
    }
  }

  return {
    kind: 'failure',
    outcome
  }
}

/**
 * Run DvPayLite integrated PRE_AUTH on device for card-on-file capture.
 *
 * @param {object} params
 * @param {string} params.refId
 * @param {number} params.amountDollars
 * @param {string|null} [params.tpn]
 * @param {object|null} [params.businessInfo]
 * @param {(() => void)|null} [params.onHandoff]
 */
export async function runIntegratedCardCaptureFlow ({
  refId,
  amountDollars,
  tpn = null,
  businessInfo = null,
  onHandoff = null
}) {
  const isvId = await fetchDvPayLiteIsvId()
  if (tpn) {
    const reg = await ensureTerminalRegistered(tpn, { businessInfo, isvId })
    if (!reg.ok) {
      return { ok: false, stage: 'register', message: reg.message }
    }
  }

  const payload = buildPreAuthCapturePayload({
    refId,
    amountDollars,
    businessInfo,
    tpn,
    isvId
  })

  onHandoff?.()
  const launchResult = await launchIntegratedSale(payload)
  const pluginResult = launchResult?.pluginResult ?? null
  const outcome = parseDvPayLitePluginResult(pluginResult)

  if (outcome.isCancelled) {
    return { ok: false, stage: 'capture', cancelled: true, message: outcome.errorMessage }
  }

  if (!outcome.isSuccess) {
    return {
      ok: false,
      stage: 'capture',
      message: outcome.errorMessage || 'integrated_capture_failed',
      launchResult,
      outcome
    }
  }

  const parsed = outcome.parsed ?? {}
  const token = parsed.iPOSToken || parsed.IPosToken || parsed.iposToken || null
  if (!token) {
    return {
      ok: false,
      stage: 'capture',
      message: 'integrated_capture_no_token',
      launchResult,
      outcome
    }
  }

  return {
    ok: true,
    parsed,
    pluginResult,
    launchResult,
    ref_id: refId,
    dvpaylite_capture: parsed
  }
}

export default {
  BLU_BRAND_DEFAULTS,
  stripHexForDvPay,
  resolveDvPayLiteBranding,
  buildCustomUi,
  formatDvPayMoney,
  buildSalePayload,
  buildPreAuthCapturePayload,
  parseDvPayLitePluginResult,
  getDvPayLitePlugin,
  isIntentTransportAvailable,
  isUriTransportAvailable,
  isIntegratedTerminalSupported,
  buildPayloadDigest,
  analyzeLaunchDiagnostics,
  buildDocExampleSalePayload,
  buildRegisterPayload,
  enrichDvPayLitePayload,
  normalizeIntentPluginResult,
  ensureTerminalRegistered,
  isTerminalRegisteredLocally,
  markTerminalRegisteredLocally,
  fetchDvPayLiteIsvId,
  launchIntegratedSale,
  buildExternalPaymentRecord,
  recordDvPayLiteExternalPayment,
  runIntegratedSaleFlow,
  runIntegratedCardCaptureFlow,
  interpretIntegratedLaunchResult
}
